import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { actionAtom } from 'atoms/atoms'
import { useFormik } from 'formik'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, Combobox, Box as Form, Input, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { usePostAddMerchantMutation } from 'hooks/mutations/usePostAddMerchantMutation'
import { usePutEditMerchantMutation } from 'hooks/mutations/usePutEditMerchantMutation'
import { useWalletsQuery } from 'hooks/queries/useWalletsQuery'
import { useActiveWallets } from 'hooks/useActiveWallets'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  ActionIds,
  AddMerchantPayload,
  EditMerchantPayload,
  Error,
  FormFieldsMerchant,
} from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface MerchantFormProps {
  action: 'EDIT' | 'CREATE'
}

export const MerchantForm = ({ action }: MerchantFormProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [sheetAction, setSheetAction] = useAtom(actionAtom)
  const merchant = sheetAction?.meta?.merchant

  const { mutate: mutateAddMerchant, isPending: isLoadingAddMerchant } =
    usePostAddMerchantMutation()
  const { mutate: mutateEditMerchant, isPending: isLoadingEditMerchant } =
    usePutEditMerchantMutation()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { data: wallets = [], isPending: isLoadingWallets } = useWalletsQuery()

  const [errors, setErrors] = useState<Error[]>([])
  const [initialValues] = useState<FormFieldsMerchant>({
    merchantName: merchant?.displayName || '',
    walletId: `${merchant?.wallet?.id}` || '',
    webhookUrl: merchant?.webhookUrl || '',
  })

  useEffect(() => {
    if (action === 'CREATE') {
      track.Amp.track(AmplitudeEvent.MERCHANT_INITIATE, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
      })
    }
    if (action === 'EDIT') {
      track.Amp.track(AmplitudeEvent.MERCHANT_EDIT, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
      })
    }
  }, [])

  const shouldDisable = (values: FormFieldsMerchant) => {
    return action === 'CREATE'
      ? Boolean(errors?.length || JSON.stringify(initialValues) === JSON.stringify(values))
      : Boolean(
          errors?.length ||
            (JSON.stringify(initialValues) === JSON.stringify(values) && merchant?.wallet)
        )
  }

  const formik = useFormik<FormFieldsMerchant>({
    initialValues,
    onSubmit: async values => {
      if (shouldDisable(values)) {
        return
      }
      resetRequestError()

      if (action === 'CREATE') {
        const merchantDetails: AddMerchantPayload = {
          displayName: values?.merchantName,
          ...(!!values?.webhookUrl && { webhookUrl: values?.webhookUrl }),
          wallet: {
            id: Number(values?.walletId),
          },
        }

        mutateAddMerchant(merchantDetails, {
          onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: reactQueryKeys.merchants() })
            track.Amp.track(AmplitudeEvent.EOF_INITIATE_MERCHANT_SUCCESS, {
              category: AmplitudeEventCategory.APPLICATION,
              action: AmplitudeEventAction.CLICK,
            })
            setSheetAction({
              ...sheetAction,
              actionId: ActionIds.MERCHANT_DETAILS,
              header: null,
              meta: {
                ...sheetAction?.meta,
                merchant: data,
              },
            })
          },
          onError: (error: BaseErrorResponse) => {
            setRequestError({
              handledMessage: t('oopsSomethingWentWrongContracted'),
              show: true,
            })
            logSentryError('Error: usePostAddMerchantMutation', error)
          },
        })
      } else {
        const merchantDetails: EditMerchantPayload = {
          displayName: values?.merchantName,
          ...(!!values?.webhookUrl && { webhookUrl: values?.webhookUrl }),
          wallet: Number(values?.walletId),
          merchant,
        }

        mutateEditMerchant(merchantDetails, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reactQueryKeys.merchants() })
            track.Amp.track(AmplitudeEvent.EOF_EDIT_MERCHANT_SUCCESS, {
              category: AmplitudeEventCategory.APPLICATION,
              action: AmplitudeEventAction.CLICK,
            })
            toast({ header: t('merchantUpdated'), variant: 'success' })
            sheetAction?.onClose()
          },
          onError: (error: BaseErrorResponse) => {
            setRequestError({
              handledMessage: t('oopsSomethingWentWrongContracted'),
              show: true,
            })
            logSentryError('Error: usePutEditMerchantMutation', error)
          },
        })
      }
    },
    validate: values => {
      setErrors([])
      if (values?.merchantName?.length > 40) {
        setErrors([
          ...errors,
          {
            code: '',
            parameter: 'merchantName',
            message: t('merchantNameLengthError'),
          },
        ])
      }
      const isWebhookUrlValid = (value: string): boolean => {
        const hasValidProtocol = value.startsWith('http://') || value.startsWith('https://')
        const hasNoSpaces = !value.includes(' ')

        return hasValidProtocol && hasNoSpaces
      }

      if (values?.webhookUrl && !isWebhookUrlValid(values?.webhookUrl)) {
        setErrors([
          ...errors,
          {
            code: '',
            parameter: 'webhookUrl',
            message: t('webhookUrlInvalidMsg'),
          },
        ])
      }
    },
  })

  const activeWallets = useActiveWallets(wallets)

  const getWalletOptions = () => {
    if (!activeWallets?.length) {
      return []
    }
    return activeWallets.map(wallet => {
      return {
        value: `${wallet?.id}`,
        leftText: wallet?.description,
        rightText: `${wallet?.balance} ${wallet?.currency?.code}`,
        LeftElement: <Avatar name={wallet?.currency?.code?.toLowerCase()} size="small" />,
      }
    })
  }

  const handleError = (field: keyof FormFieldsMerchant) => {
    const error = errors?.find(err => err.parameter === field)
    return error?.message
  }

  return (
    <Form tag="form" width="full" flexCol gapY={16} onSubmit={formik?.handleSubmit}>
      <Input
        testId="merchant-name"
        label={t('merchantName')}
        placeholderText={t('merchantName')}
        name="merchantName"
        onChange={formik?.handleChange}
        value={formik?.values?.merchantName}
        error={Boolean(handleError('merchantName'))}
        errorText={handleError('merchantName')}
        required
      />
      <Combobox
        id="walletId"
        label={t('wallet')}
        onChange={(value: string) => {
          formik.setFieldValue('walletId', value)
        }}
        loading={isLoadingWallets}
        items={getWalletOptions()}
        defaultValue={formik?.values?.walletId}
      />
      <Input
        testId="merchant-webhook"
        label={t('webhookUrl')}
        optionalText={t('optionalFld')}
        placeholderText="https://"
        name="webhookUrl"
        onChange={formik?.handleChange}
        value={formik?.values?.webhookUrl}
        error={Boolean(handleError('webhookUrl'))}
        errorText={handleError('webhookUrl')}
      />
      {requestError?.show && <RequestError>{requestError?.handledMessage}</RequestError>}
      <Box flex justify="between" paddingT={16}>
        <Button testId="back" variant="secondary" onClick={sheetAction?.onClose}>
          {t('cancel')}
        </Button>
        <Button
          testId="merchant-save-btn"
          type="submit"
          variant="primary"
          loading={action === 'CREATE' ? isLoadingAddMerchant : isLoadingEditMerchant}
          disabled={shouldDisable(formik?.values)}
        >
          {action === 'CREATE' ? t('createMerchant') : t('save')}
        </Button>
      </Box>
    </Form>
  )
}
